// @flow
import React, { Component, createRef } from "react";
import ReactTable, { ReactTableDefaults } from "react-table";
import AlertContainer from "react-alert";
import { Icon, Label, Grid, Button } from "semantic-ui-react";
import { withTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import moment from "moment";

import BrandDetail from "./BrandDetail";
import BrandGroupDetail from "./BrandGroupDetail";
import BrandGroupsFilter from "./BrandGroupsFilter";
import { SubHeader } from "./../../components";
import Service from "./service";
import CustomConfirm from "../../components/CustomConfirm";
import { getMaxFileSizeError, isFileSizeExceeded } from "../../util/common";
import Can from "../Can";
import "./Brands.css";

const BRAND_USED_ON_USERS_ERROR = /brand cannot be deleted, still used on \d+ users/;

const sortByName = (a, b) => (a.name || "").localeCompare(b.name || "");

const fillGroupSettings = (brand, group) => ({
  ...brand,
  country: brand.country || group.country,
  color: brand.color || group.color,
  logo: brand.logo || group.logo,
  button_label: brand.button_label || group.button_label,
  tablet_service_button_url: brand.tablet_service_button_url || group.tablet_service_button_url,
  servicebox_wo: brand.servicebox_wo || group.servicebox_wo,
  servicebox_vin: brand.servicebox_vin || group.servicebox_vin,
  servicebox_wo_2: brand.servicebox_wo_2 || group.servicebox_wo_2,
  servicebox_wo_2_name: brand.servicebox_wo_2_name || group.servicebox_wo_2_name,
});

const clearUnchangedGroupSettings = (brand, group) => {
  if (!group) return brand;

  return {
    ...brand,
    country: brand.country === group.country ? "" : brand.country,
    button_label: brand.button_label === group.button_label ? "" : brand.button_label,
    color: brand.color === group.color ? "" : brand.color,
    logo: brand.logo === group.logo ? "" : brand.logo,
    tablet_service_button_url: brand.tablet_service_button_url === group.tablet_service_button_url ? "" : brand.tablet_service_button_url,
    servicebox_wo: brand.servicebox_wo === group.servicebox_wo ? "" : brand.servicebox_wo,
    servicebox_vin: brand.servicebox_vin === group.servicebox_vin ? "" : brand.servicebox_vin,
    servicebox_wo_2: brand.servicebox_wo_2 === group.servicebox_wo_2 ? "" : brand.servicebox_wo_2,
    servicebox_wo_2_name: brand.servicebox_wo_2_name === group.servicebox_wo_2_name ? "" : brand.servicebox_wo_2_name,
  };
};

class Brands extends Component {
  state = {
    brands: [],
    brandGroups: [],
    selectedVinPrefixes: [],
    deleteError: null,
    selectedBrand: {},
    isLoadingBrands: false,
    isSubmittingDetail: false,
    isBrandDetailVisible: false,
    isDeleteConfirmationVisible: false,
    detailViewMode: "",
    isDeletingBrand: false,
    isForceDeleteConfirmed: false,
    selectedBrandGroup: {},
    isBrandGroupDetailVisible: false,
    brandGroupDetailViewMode: "",
    isSubmittingBrandGroup: false,
    expanded: {},
  };

  msg = createRef();
  importFileInput = createRef();

  componentDidMount() {
    this.getBrands();
    this.getBrandGroups();
  }

  renderAlert = () => {
    const props = {
      offset: 20,
      position: "top right",
      theme: "light",
      time: 10000,
      transition: "fade",
    };

    return (
      <div className="react-alert-container">
        <AlertContainer ref={this.msg} {...props} />
      </div>
    );
  };

  getBrands = () => {
    if (this.state.isLoadingBrands) {
      return;
    }
    this.setState(
      {
        isLoadingBrands: true,
      },
      () => {
        Service.getBrands()
          .then(result => {
            if (result?.data?.data?.brands) {
              this.setState({ brands: result.data.data.brands, isLoadingBrands: false });
            }
          })
          .catch(error => {
            console.error("Error getting news brands.", error);
            this.setState({ brands: [], isLoadingBrands: false });
          });
      }
    );
  };

  getBrandGroups = () => {
    Service.getBrandGroups()
      .then(result => {
        if (result?.data?.data?.brand_groups) {
          this.setState({ brandGroups: result.data.data.brand_groups });
        }
      })
      .catch(error => {
        console.error("Error getting brand groups.", error);
        this.setState({ brandGroups: [] });
      });
  };

  getBrandGroupOptions = () => {
    return this.state.brandGroups.map(group => ({ key: group.id, value: group.id, text: group.name }));
  };

  getFilteredBrandGroups = selectedVinPrefixes => {
    const { brands, brandGroups } = this.state;

    return [...brandGroups].sort(sortByName).reduce((groups, group) => {
      const groupBrands = brands
        .filter(brand => brand.brand_group_id === group.id && (!selectedVinPrefixes.length || selectedVinPrefixes.includes(brand.vin_prefix)))
        .sort(sortByName);

      if (selectedVinPrefixes.length && !groupBrands.length) return groups;
      return [...groups, { ...group, brands: groupBrands }];
    }, []);
  };

  handleFilterChange = selectedVinPrefixes => {
    const expanded = selectedVinPrefixes.length ? Object.fromEntries(this.getFilteredBrandGroups(selectedVinPrefixes).map((_, index) => [index, {}])) : {};
    this.setState({ selectedVinPrefixes, expanded });
  };

  handleDeleteBrand = () => {
    let { selectedBrand, isLoadingBrands, isForceDeleteConfirmed } = this.state;

    if (isLoadingBrands || !(selectedBrand && selectedBrand.id)) {
      return;
    }

    this.setState(
      {
        isLoadingBrands: true,
        isDeletingBrand: true,
      },
      () => {
        Service.deleteBrand({ id: selectedBrand.id, is_confirmed: isForceDeleteConfirmed })
          .then(() => {
            this.setState(
              { isDeleteConfirmationVisible: false, selectedBrand: {}, deleteError: null, isForceDeleteConfirmed: false, isLoadingBrands: false, isDeletingBrand: false },
              () => {
                this.getBrands();
              }
            );
          })
          .catch(error => {
            let err = error.response && error.response.data && error.response.data.errors ? error.response.data.errors[0] : "Error deleting brand";

            if (!isForceDeleteConfirmed && BRAND_USED_ON_USERS_ERROR.test(err)) {
              this.setState({ deleteError: err, isForceDeleteConfirmed: true, isLoadingBrands: false, isDeletingBrand: false });
              return;
            }

            this.setState({ deleteError: err, isLoadingBrands: false, isDeletingBrand: false });
          });
      }
    );
  };

  handleHideDeleteConfirmation = () => {
    this.setState({ isDeleteConfirmationVisible: false, selectedBrand: {}, deleteError: null, isForceDeleteConfirmed: false, isLoadingBrands: false });
  };

  handleShowDeleteConfirmation = brand => {
    this.setState({ isDeleteConfirmationVisible: true, selectedBrand: brand, deleteError: null, isForceDeleteConfirmed: false });
  };

  handleShowBrandDetail = options => {
    this.setState({
      detailViewMode: options.mode,
      isBrandDetailVisible: true,
      isSubmittingDetail: false,
      selectedBrand: { ...options.selectedBrand },
    });
  };

  handleHideBrandDetail = () => {
    this.setState({ isBrandDetailVisible: false, selectedBrand: {}, isSubmittingDetail: false, isLoadingBrands: false });
  };

  handleSaveBrand = (brand, photo) => {
    if (!brand.name.trim()) return;

    this.setState({ isSubmittingDetail: true });
    this.saveWithLogo(brand, photo, `${brand.vin_prefix}_${brand.name}`, this.submitBrand, () => this.setState({ isSubmittingDetail: false }));
  };

  handleShowBrandGroupDetail = options => {
    const { brands, ...selectedBrandGroup } = options.selectedBrandGroup;

    this.setState({
      brandGroupDetailViewMode: options.mode,
      isBrandGroupDetailVisible: true,
      isSubmittingBrandGroup: false,
      selectedBrandGroup,
    });
  };

  handleHideBrandGroupDetail = () => {
    this.setState({ isBrandGroupDetailVisible: false, selectedBrandGroup: {}, isSubmittingBrandGroup: false });
  };

  handleSaveBrandGroup = (group, photo) => {
    this.setState({ isSubmittingBrandGroup: true });
    this.saveWithLogo(group, photo, group.name, this.submitBrandGroup, () => this.setState({ isSubmittingBrandGroup: false }));
  };

  saveWithLogo = (entity, photo, path, submit, onFailed) => {
    if (!photo) {
      submit(entity);
      return;
    }

    if (isFileSizeExceeded(photo)) {
      this.msg.current.show(getMaxFileSizeError(this.props.t), { type: "error" });
      onFailed();
      return;
    }

    this.uploadLogo(photo, path)
      .then(response => {
        if (!response?.data?.data?.url) throw new Error(this.props.t("failed_to_upload_image").message || "Failed to upload an image");

        submit({ ...entity, logo: response.data.data.url });
      })
      .catch(error => {
        console.error("Error uploading the logo.", error);

        const errorMsg = error?.response?.data?.errors?.[0] || this.props.t("failed_to_upload_image").message || "Failed to upload an image";

        this.msg.current.show(errorMsg, { type: "error" });
        onFailed();
      });
  };

  uploadLogo = (file, path) => {
    if (!file) return;

    let fd = new FormData();

    fd.append("file", file);
    fd.append("path", path);

    return Service.uploadBrandLogo(fd);
  };

  handleImportFileButtonClick = () => {
    this.importFileInput.current.click();
  };

  handleImportFileChange = event => {
    const file = event.target.files[0];
    if (!file) return;

    const isODS = file.name.toLowerCase().endsWith(".ods") && file.type === "application/vnd.oasis.opendocument.spreadsheet";

    if (!isODS) {
      this.msg.current.show(this.props.t("file_must_be_ods").message || "The file must be an .ods file", { type: "error" });
      return;
    }

    if (isFileSizeExceeded(file)) {
      this.msg.current.show(getMaxFileSizeError(this.props.t), { type: "error" });
      this.importFileInput.current.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    Service.importBrands(formData)
      .then(() => {
        this.msg.current.show(this.props.t("import_success").message || "Import Success", { type: "success" });
        this.getBrands();
        this.getBrandGroups();
        this.importFileInput.current.value = "";
      })
      .catch(error => {
        const errorMsg =
          typeof error.response?.data === "string" ? error.response.data : error.response.data?.errors?.[0] ?? (this.props.t("failed_import").message || "Failed Import");

        this.msg.current.show(errorMsg, { type: "error" });
        this.importFileInput.current.value = "";
      });
  };

  exportBrands = async () => {
    Service.exportBrands()
      .then(response => {
        if (response?.data?.data?.url) saveAs(response.data.data.url, `Claire_Brands_Export_${moment().format("YYYYMMDDhhss")}.ods`);
      })
      .catch(error => {
        console.error("Error Getting Export URL", error);
      });
  };

  submitBrand = brand => {
    if (this.state.detailViewMode === "create") {
      this.setState(
        {
          isSubmittingDetail: true,
        },
        () => {
          Service.createBrand(
            clearUnchangedGroupSettings(
              brand,
              this.state.brandGroups.find(group => group.id === brand.brand_group_id)
            )
          )
            .then(() => {
              this.handleHideBrandDetail();
              this.getBrands();
            })
            .catch(error => {
              console.error("Error on submitting brand.", error);
              this.setState({ isSubmittingDetail: false });
            });
        }
      );
    } else {
      this.setState(
        {
          isSubmittingDetail: true,
        },
        () => {
          Service.updateBrand(
            clearUnchangedGroupSettings(
              brand,
              this.state.brandGroups.find(group => group.id === brand.brand_group_id)
            )
          )
            .then(() => {
              this.handleHideBrandDetail();
              this.getBrands();
            })
            .catch(error => {
              console.error("Error on submitting brand.", error);
              this.setState({ isSubmittingDetail: false });
            });
        }
      );
    }
  };

  submitBrandGroup = group => {
    const request = this.state.brandGroupDetailViewMode === "create" ? Service.createBrandGroup(group) : Service.updateBrandGroup(group);

    request
      .then(() => {
        this.handleHideBrandGroupDetail();
        this.setState({ expanded: {} });
        this.getBrands();
        this.getBrandGroups();
      })
      .catch(error => {
        console.error("Error on submitting brand group.", error);
        this.setState({ isSubmittingBrandGroup: false });
      });
  };

  renderDeleteConfirm = () => {
    const { isForceDeleteConfirmed, deleteError } = this.state;

    const confirmMsg = isForceDeleteConfirmed
      ? `${deleteError.charAt(0).toUpperCase() + deleteError.slice(1)}. ${this.props.t("delete_anyway").message || "Delete anyway?"}`
      : this.props.t("confirm_delete_message").message || "Are you sure that you want to delete this? You can't undo this action.";

    return (
      <CustomConfirm
        type="danger"
        isOpen={this.state.isDeleteConfirmationVisible}
        handleCancel={this.handleHideDeleteConfirmation}
        handleConfirm={() => this.handleDeleteBrand()}
        isLoading={this.state.isDeletingBrand}
        confirmMsg={confirmMsg}
        error={isForceDeleteConfirmed ? null : deleteError}
      />
    );
  };

  renderTable = () => {
    let { isLoadingBrands } = this.state;
    let { t } = this.props;

    if (isLoadingBrands) {
      return (
        <div className="Table__loading Loader-Placeholder">
          <div className="bounce1"></div>
          <div className="bounce2"></div>
          <div className="bounce3"></div>
          <section>{t("loading_brands").message || "Loading brands"}</section>
        </div>
      );
    } else {
      const { selectedVinPrefixes, expanded } = this.state;
      const filteredBrandGroups = this.getFilteredBrandGroups(selectedVinPrefixes);

      return (
        <div className="BrandsTable">
          <ReactTable
            className="ReactTable -highlight"
            data={filteredBrandGroups}
            showPagination={false}
            showPageSizeOptions={false}
            sortable={false}
            resizable={false}
            defaultPageSize={filteredBrandGroups.length}
            pageSize={filteredBrandGroups.length}
            noDataText={
              <div className="Table__no-results">
                <Icon disabled name="car" style={{ fontSize: "1.75em" }} />
                <p>{t("no_brands").message || "No brands"}</p>
              </div>
            }
            getTdProps={(state, rowInfo, column) => ({
              onClick: (e, handleOriginal) => {
                if (rowInfo && !column.expander && column.id !== "actions") this.handleShowBrandGroupDetail({ mode: "edit", selectedBrandGroup: rowInfo.original });
                if (handleOriginal) handleOriginal();
              },
            })}
            getTrProps={(state, rowInfo) => {
              if (!rowInfo) {
                return {};
              }
              return {
                style: {
                  borderLeft: `4px solid ${rowInfo.original.is_active ? "green" : "orange"}`,
                },
              };
            }}
            column={{
              ...ReactTableDefaults.column,
              headerClassName: "ReactTable__column-header",
              className: "ReactTable__column",
            }}
            columns={[
              {
                id: "name",
                Header: t("group").message || "Group",
                accessor: "name",
                width: 300,
                maxWidth: 300,
              },
              {
                id: "brand_count",
                Header: t("brand_count").message || "Brand count",
                accessor: d => d.brands.length,
              },
              {
                id: "actions",
                Header: "",
                accessor: d => (
                  <div className="-pull-right">
                    <Can I="create" the="brands">
                      <Icon
                        color="green"
                        name="plus circle"
                        size="large"
                        onClick={() =>
                          this.handleShowBrandDetail({ mode: "create", selectedBrand: fillGroupSettings({ name: "", vin_prefix: "", brand_group_id: d.id }, d) })
                        }
                      />
                    </Can>
                  </div>
                ),
                maxWidth: 80,
              },
            ]}
            expanded={expanded}
            onExpandedChange={expanded => this.setState({ expanded })}
            SubComponent={row => (
              <div className="brand-group-brands">
                <ReactTable
                  className="ReactTable -highlight"
                  data={row.original.brands}
                  showPagination={false}
                  showPageSizeOptions={false}
                  sortable={false}
                  resizable={false}
                  minRows={0}
                  pageSize={row.original.brands.length}
                  noDataText={t("no_brands").message || "No brands"}
                  getTdProps={(state, rowInfo, column) => ({
                    onClick: () => {
                      if (rowInfo && column.id !== "delete")
                        this.handleShowBrandDetail({ mode: "edit", selectedBrand: fillGroupSettings(rowInfo.original, row.original) });
                    },
                  })}
                  getTrProps={(state, rowInfo) => {
                    if (!rowInfo) {
                      return {};
                    }
                    return {
                      style: {
                        borderLeft: `4px solid ${rowInfo.original.is_active ? "green" : "orange"}`,
                      },
                    };
                  }}
                  column={{
                    ...ReactTableDefaults.column,
                    headerClassName: "ReactTable__column-header",
                    className: "ReactTable__column",
                  }}
                  columns={[
                    {
                      id: "name",
                      Header: t("brand").message || "Brand",
                      accessor: "name",
                      width: 305,
                      maxWidth: 305,
                    },
                    {
                      id: "vin_prefix",
                      Header: t("vin_nr").message || "Vin #",
                      accessor: "vin_prefix",
                      width: 250,
                      maxWidth: 250,
                    },
                    {
                      id: "button_label",
                      Header: t("button_label").message || "Button label",
                      accessor: "button_label",
                    },
                    {
                      id: "delete",
                      Header: "",
                      className: "icons-snoozed",
                      accessor: d => (
                        <Can I="delete" the="brands">
                          <Label color="red" className="BrandDeleteLabel" onClick={() => this.handleShowDeleteConfirmation(d)}>
                            <Icon link name="trash" />
                          </Label>
                        </Can>
                      ),
                      maxWidth: 55,
                    },
                  ]}
                />
              </div>
            )}
          />
        </div>
      );
    }
  };

  render() {
    return (
      <div className="Brand page_inner_wrapper">
        <SubHeader>
          <Grid.Column width={11}>
            <h1>{this.props.t("brands").message || "Brands"}</h1>
          </Grid.Column>
          <Grid.Column width={5} floated="right" className="brand-header-actions">
            <Can I="import" the="brands">
              <input type="file" ref={this.importFileInput} style={{ display: "none" }} onChange={this.handleImportFileChange} />
              <Button color="green" onClick={this.handleImportFileButtonClick}>
                {this.props.t("import").message || "Import"}
              </Button>
            </Can>
            <Can I="export" the="brands">
              <Button color="green" onClick={this.exportBrands}>
                {this.props.t("export").message || "Export"}
              </Button>
            </Can>
            <Can I="create" the="brand-groups">
              <Button color="green" icon labelPosition="left" onClick={() => this.handleShowBrandGroupDetail({ mode: "create", selectedBrandGroup: { name: "" } })}>
                <Icon name="add" /> {this.props.t("add").message || "Add"}
              </Button>
            </Can>
          </Grid.Column>
          <Grid.Column width={16} className="brands-filter-row">
            <div className="brands-filter">
              <BrandGroupsFilter
                brands={this.state.brands}
                brandGroups={this.state.brandGroups}
                value={this.state.selectedVinPrefixes}
                onChange={this.handleFilterChange}
              />
            </div>
          </Grid.Column>
        </SubHeader>

        {this.renderTable()}
        {this.state.isDeleteConfirmationVisible && this.renderDeleteConfirm()}
        {this.state.isBrandDetailVisible && (
          <BrandDetail
            mode={this.state.detailViewMode}
            loading={this.state.isSubmittingDetail}
            brand={this.state.selectedBrand}
            brands={this.state.brands}
            brandGroupOptions={this.getBrandGroupOptions()}
            onHideDetail={this.handleHideBrandDetail}
            onSave={this.handleSaveBrand}
          />
        )}
        {this.state.isBrandGroupDetailVisible && (
          <BrandGroupDetail
            mode={this.state.brandGroupDetailViewMode}
            loading={this.state.isSubmittingBrandGroup}
            group={this.state.selectedBrandGroup}
            brandGroups={this.state.brandGroups}
            onHideDetail={this.handleHideBrandGroupDetail}
            onSave={this.handleSaveBrandGroup}
          />
        )}

        {this.renderAlert()}
      </div>
    );
  }
}

export default withTranslation()(Brands);
